<script setup lang="ts">
import { AlignLeft, ArrowLeft, ArrowRightLeft, Braces, Check, CheckCircle2, Copy, Download, FileCode2, RotateCcw, ShieldCheck, TriangleAlert } from "@lucide/vue";
import { computed, ref, watch } from "vue";
import { analyzeData, DATA_EXAMPLES, MAX_INPUT_BYTES, type DataFormat } from "../lib/dataWorkbench";

const format = ref<DataFormat>("json");
const indent = ref<2 | 4>(2);
const source = ref(DATA_EXAMPLES.json);
const copyState = ref<"idle" | "done" | "error">("idle");
const analysis = computed(() => analyzeData(source.value, format.value, indent.value));
const output = computed(() => analysis.value.convertedOutput);
const outputLabel = computed(() => analysis.value.outputFormat.toUpperCase());
const sizeLabel = computed(() => analysis.value.stats.bytes < 1024
  ? `${analysis.value.stats.bytes} B`
  : `${(analysis.value.stats.bytes / 1024).toFixed(1)} KiB`);

watch([source, format, indent], () => {
  copyState.value = "idle";
});

function selectFormat(next: DataFormat) {
  format.value = next;
  source.value = DATA_EXAMPLES[next];
}

function reset() {
  source.value = DATA_EXAMPLES[format.value];
  copyState.value = "idle";
}

function formatInput() {
  if (analysis.value.ok) source.value = analysis.value.formattedInput;
}

function useOutputAsInput() {
  if (!analysis.value.ok) return;
  source.value = analysis.value.convertedOutput;
  format.value = analysis.value.outputFormat;
}

async function copyOutput() {
  if (!analysis.value.ok) return;
  try {
    await navigator.clipboard.writeText(output.value);
    copyState.value = "done";
  } catch {
    copyState.value = "error";
  }
}

function downloadOutput() {
  if (!analysis.value.ok) return;
  const extension = analysis.value.outputFormat === "json" ? "json" : "yaml";
  const mime = extension === "json" ? "application/json" : "application/yaml";
  const blob = new Blob([output.value], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `vpscope-converted.${extension}`;
  anchor.click();
  URL.revokeObjectURL(url);
}
</script>

<template>
  <header class="tool-head data-tool-head">
    <div class="page-width tool-head-inner">
      <div>
        <RouterLink class="tool-back" to="/tools"><ArrowLeft :size="15" />工具与资源</RouterLink>
        <span class="tool-kicker"><Braces :size="15" />配置数据</span>
        <h1>JSON / YAML 工作台</h1>
        <p>校验、格式化并互相转换配置数据，遇到不确定或有损结果时明确拒绝。</p>
      </div>
      <div class="local-compute"><ShieldCheck :size="18" /><span><strong>浏览器本地处理</strong><small>内容不会上传</small></span></div>
    </div>
  </header>

  <section class="tool-workbench data-workbench-band">
    <div class="page-width">
      <div class="data-toolbar" aria-label="工作台设置">
        <div class="format-switch" aria-label="输入格式">
          <button type="button" :class="{ active: format === 'json' }" @click="selectFormat('json')"><Braces :size="15" />JSON</button>
          <button type="button" :class="{ active: format === 'yaml' }" @click="selectFormat('yaml')"><FileCode2 :size="15" />YAML</button>
        </div>
        <div class="indent-switch" aria-label="缩进空格">
          <span>缩进</span>
          <button type="button" :class="{ active: indent === 2 }" @click="indent = 2">2</button>
          <button type="button" :class="{ active: indent === 4 }" @click="indent = 4">4</button>
        </div>
        <div class="data-toolbar-actions">
          <button type="button" title="格式化输入" :disabled="!analysis.ok" @click="formatInput"><AlignLeft :size="16" /><span>格式化</span></button>
          <button type="button" title="恢复示例" @click="reset"><RotateCcw :size="16" /><span>重置</span></button>
        </div>
      </div>

      <div class="data-editor-shell">
        <section class="data-pane source-pane" :class="{ invalid: !analysis.ok }">
          <header>
            <div><span>输入</span><strong>{{ format.toUpperCase() }}</strong></div>
            <small>{{ sizeLabel }} · {{ analysis.stats.lines }} 行</small>
          </header>
          <label>
            <span class="sr-only">{{ format.toUpperCase() }} 输入</span>
            <textarea v-model="source" :aria-invalid="!analysis.ok" :aria-describedby="analysis.ok ? 'data-valid-status' : 'data-error-status'" wrap="off" spellcheck="false" autocomplete="off" />
          </label>
        </section>

        <div class="data-transfer">
          <button type="button" :disabled="!analysis.ok" title="将结果作为新输入" aria-label="将结果作为新输入" @click="useOutputAsInput"><ArrowRightLeft :size="17" /></button>
        </div>

        <section class="data-pane output-pane">
          <header>
            <div><span>转换结果</span><strong>{{ outputLabel }}</strong></div>
            <div class="pane-actions">
              <button type="button" :disabled="!analysis.ok" :title="copyState === 'done' ? '已复制' : '复制结果'" :aria-label="copyState === 'done' ? '已复制' : '复制结果'" @click="copyOutput"><Check v-if="copyState === 'done'" :size="16" /><Copy v-else :size="16" /></button>
              <button type="button" :disabled="!analysis.ok" title="下载结果" aria-label="下载结果" @click="downloadOutput"><Download :size="16" /></button>
            </div>
          </header>
          <label>
            <span class="sr-only">{{ outputLabel }} 转换结果</span>
            <textarea :value="output" readonly wrap="off" spellcheck="false" :placeholder="analysis.ok ? '' : '修正输入后显示转换结果'" />
          </label>
        </section>
      </div>

      <div v-if="analysis.ok" id="data-valid-status" class="data-status valid" aria-live="polite">
        <CheckCircle2 :size="17" />
        <strong>结构有效</strong>
        <span>{{ analysis.stats.rootType }} · {{ analysis.stats.nodes }} 个节点 · 深度 {{ analysis.stats.depth }}</span>
      </div>
      <div v-else id="data-error-status" class="data-status invalid" aria-live="polite">
        <TriangleAlert :size="17" />
        <div><strong>暂不转换</strong><p v-for="issue in analysis.issues.slice(0, 3)" :key="`${issue.code}-${issue.line}-${issue.column}`">第 {{ issue.line }} 行，第 {{ issue.column }} 列：{{ issue.message }}</p></div>
      </div>

      <div class="data-boundaries" aria-label="处理边界">
        <span><ShieldCheck :size="14" />严格 JSON / YAML 1.2 Core</span>
        <span>最大 512 KiB</span>
        <span>最大深度 64</span>
        <span>YAML 别名展开上限 20</span>
      </div>
    </div>
  </section>

  <section class="tool-sources">
    <div class="page-width">
      <span>格式依据</span>
      <p>语法与兼容边界依据开放标准；解析由受维护的库在浏览器内完成。</p>
      <div>
        <a href="https://www.rfc-editor.org/rfc/rfc8259.html" target="_blank" rel="noreferrer">RFC 8259 · JSON</a>
        <a href="https://yaml.org/spec/1.2.2/" target="_blank" rel="noreferrer">YAML 1.2.2</a>
        <a href="https://github.com/microsoft/node-jsonc-parser" target="_blank" rel="noreferrer">jsonc-parser 3.3.1</a>
        <a href="https://eemeli.org/yaml/" target="_blank" rel="noreferrer">yaml 2.9.0</a>
      </div>
    </div>
  </section>
</template>
